import { useMemo } from 'react';
import { toIssueId, toIssueTypeId } from '@atlassian/jira-shared-types/src/general.tsx';
import { NO_EPIC_DISPLAYED_NAME } from '../../common/constants';
import type { TopFilter } from '../../common/types';
import { useTableFilterStore, initialState } from '../top-filter';
import { QUERY_PARAM_ISSUE_TYPE, QUERY_PARAM_EPIC } from './constants';
import type { QueryParams, SearchQueryParams } from './types';

const DELIMITER = ',';
const ENCODED_DELIMITER = encodeURIComponent(DELIMITER);

// we need to encode the delimiter to protect our free text values from containing
// the same character that delimits the list.
// e.g.
// env names = ["prod,east", "prod,west"] -> "prod,east,prod,west"
// @ts-expect-error - TS2304 - Cannot find name 'IssueId'. | TS2304 - Cannot find name 'IssueTypeId'.
const encodeDelimeter = (input: IssueId | IssueTypeId) => {
	const regex = new RegExp(DELIMITER, 'g');
	return input.replace(regex, ENCODED_DELIMITER);
};

const decodeDelimeter = (input: string) => {
	const regex = new RegExp(ENCODED_DELIMITER, 'g');
	return input.replace(regex, DELIMITER);
};

// @ts-expect-error - TS2304 - Cannot find name 'IssueTypeId'. | TS2304 - Cannot find name 'EpicIdOption'.
const filterToQueryParam = (arr: Array<IssueTypeId> | Array<EpicIdOption>) => {
	if (arr.length === 0) return undefined;
	return arr
		.map((entity) => (entity === null ? NO_EPIC_DISPLAYED_NAME : encodeDelimeter(entity)))
		.join(DELIMITER);
};

const queryParamToFilter = (str: string) => str.split(DELIMITER).map(decodeDelimeter);

const queryParamToFilterNullable = (str: string) =>
	str
		.split(DELIMITER)
		.map(decodeDelimeter)
		.map((x) => (x === NO_EPIC_DISPLAYED_NAME ? null : x));

export const getFiltersFromQueryParams = (queryParams: SearchQueryParams): TopFilter => {
	const topFilterState: TopFilter = {
		...initialState,
	};

	if (queryParams[QUERY_PARAM_EPIC] != null) {
		topFilterState.epicIds = queryParamToFilterNullable(queryParams[QUERY_PARAM_EPIC]).map(
			(epicId) => (epicId === null ? null : toIssueId(epicId)),
		);
	}

	if (queryParams[QUERY_PARAM_ISSUE_TYPE] != null) {
		topFilterState.issueTypeIds = queryParamToFilter(queryParams[QUERY_PARAM_ISSUE_TYPE]).map(
			(issueTypeId) => toIssueTypeId(issueTypeId),
		);
	}

	return topFilterState;
};

export const getQueryParamsFromFilters = (filters: TopFilter): QueryParams => ({
	[QUERY_PARAM_EPIC]: filterToQueryParam(filters.epicIds),
	[QUERY_PARAM_ISSUE_TYPE]: filterToQueryParam(filters.issueTypeIds),
});

export const useQueryParamsFromFilters = (): QueryParams => {
	const [filters] = useTableFilterStore();
	return useMemo(() => getQueryParamsFromFilters(filters), [filters]);
};
